import { today } from '@/lib/format'

/* Готовые срезы реестра.

   Вопросы «что просрочено», «что сдаём на неделе», «где нет файла» задаются
   каждый день, а собирались руками — точным совпадением по дате их не задать
   вовсе. Сервер понимает окно (`range:с..по`) и «не заполнено» (`empty:`), см.
   RegisterRepository.where; здесь эти две формы складываются в срез. */
export type Slice = '' | 'overdue' | 'week' | 'nofile' | 'nocode' | 'ifa-week' | 'afd-week' | 'behind'

const iso = (d: Date): string =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`

const shift = (days: number): string => {
  const d = today()
  d.setDate(d.getDate() + days)
  return iso(d)
}

/* Состояния, по которым работа идёт. Просрочка считается только по ним — так
   её считает и сервер (DashboardRepository.OVERDUE, счётчик реестра, обход
   сроков): аннулированный, приостановленный и заменённый документ никого не
   ждёт. Без этого условия рельса реестра показывала «● 12», а срез
   «Просрочено» — 18 строк, и расхождение объясняли на каждом совещании.
   Набор закрыт схемой (V70), поэтому перечисляется здесь целиком. */
const LIVE = ['planned', 'received', 'review', 'crs', 'reissue', 'approved', 'accepted', 'afd']

/* Пересечение выбранного состояния с состояниями среза. Выбрать
   «Аннулированные» и «Просрочено» разом можно, и ответ на это — пусто, а не
   молча снятое условие: значение, которого нет ни у одной строки. */
const NONE = ['\u0000']

const lifeOf = (life?: string): string[] =>
  !life ? LIVE : LIVE.includes(life) ? [life] : NONE

/**
 * Фильтры среза. Выбранное состояние приходит сюда же: у срезов просрочки оно
 * пересекается со «живыми» состояниями, а не подменяет их и не подменяется.
 */
export function sliceFilters(slice: Slice, life?: string): Record<string, string[]> {
  const own: Record<string, string[]> = life ? { life: [life] } : {}
  switch (slice) {
    // Просрочено — срок прошёл, а факта нет. Сегодняшний срок ещё не просрочен.
    case 'overdue':
      return { plan: [`range:..${shift(-1)}`], fact: ['empty:'], life: lifeOf(life) }
    case 'week':
      return { plan: [`range:${shift(0)}..${shift(7)}`], fact: ['empty:'], life: lifeOf(life) }
    /* Стадийные сроки: общий срок документа — это срок его ДЕЙСТВУЮЩЕЙ стадии,
       и вопрос «что уходит на согласование на неделе» по нему не задаётся.
       Сервер сравнивает исходную дату стадии, а не показанный текст: по тексту
       окно роняло запрос (planIfa отдавался сразу to_char'ом). */
    case 'ifa-week':
      return { planIfa: [`range:${shift(0)}..${shift(7)}`], factIfa: ['empty:'], life: lifeOf(life) }
    case 'afd-week':
      return { planAfd: [`range:${shift(0)}..${shift(7)}`], factAfd: ['empty:'], life: lifeOf(life) }
    /* Отставание от контрактного графика: день и больше от базового плана.
       База ставится согласованием реестра и дальше не двигается — иначе после
       каждого изменения отклонение обнулялось бы, а документ уезжал. */
    case 'behind':
      return { planDrift: ['range:1..'], ...own }
    case 'nofile':
      return { hasMain: ['false'], ...own }
    case 'nocode':
      return { code: ['empty:'], ...own }
    default:
      return own
  }
}

/**
 * Пересечение двух отборов. Срез сверху и галочки в шапке смотрят на одни и те
 * же колонки: список значений на сервере — ИЛИ внутри колонки, поэтому
 * подстановка одного вместо другого показала бы строки, которых срез не
 * включает. Пересечение пустое — значение, которого нет ни у одной строки: то
 * же правило, что у среза с выбранным состоянием.
 */
export function intersectFilters(
  base: Record<string, string[]>,
  extra: Record<string, string[]>,
): Record<string, string[]> {
  const out = { ...base }
  for (const [col, values] of Object.entries(extra)) {
    if (!values.length) continue
    const already = out[col]
    const both = already ? values.filter((v) => already.includes(v)) : values
    out[col] = both.length ? both : NONE
  }
  return out
}
